/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel, PopupRequest } from "@azure/msal-browser";

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */

export const msalConfig = {
  auth: {
    clientId: "09e506f1-4a89-44d9-80ed-873c0a7d4af5",
    // clientId: "d59fced2-20f2-4bf5-9403-c4d19ba9a104",
    authority:
      "https://login.microsoftonline.com/7fc6be4a-81b6-457d-9ad7-c8d5c64d052c",
    redirectUri: process.env.REACT_APP_REDIRECT_URI,
  },
  cache: {
    cacheLocation: "sessionStorage", // This configures where your cache will be stored
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (
        level: LogLevel,
        message: string,
        containsPii: boolean
      ) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            // console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          default:
            return;
        }
      },
    },
  },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest: PopupRequest = {
  scopes: [
    "api://ac5774c7-d71a-43e7-9a08-36b3a5f41969/Georges.Read",
    "Mail.Send" 
  ],
};

// Define the access groups and their corresponding Azure AD group IDs
export const accessGroups = {
  infra: {
    groupIds: [
      "0c88b31d-a8f9-4bc5-8391-03fb031238d4",
      "39ee0ff2-da33-4993-ae8e-eed21b8af8e4",
      "6ca178c3-49c7-4744-bb3d-9aa4ce15d448",
      "d27108f5-050e-4472-82eb-9faa8f355a10",
      // "77205305-d051-44e4-90f4-9a2e9af8418c", // to delete, test group
    ],
    pages: ["market", "mydeal"],
  },
  fmc: {
    groupIds: [
      "2ecccd55-0749-46e7-8bfb-cc1212d026e7",
      "97c55c12-43e9-4c46-b5ce-ef29c2217309",
      "c5a8bef5-32b6-46eb-819f-80fafa670ef8",
      "32aa2d4c-5593-4504-995a-2d5f4ab58f3e",
    ],
    pages: ["market", "mydeal"],
  },
  growth: {
    groupIds: [
      "5ba41348-2cb1-47f0-a361-75612bb7ced3",
      "6ff9d810-539c-4c88-bf6a-c5d1da7f131a",
      "9daf3e59-48ed-4810-aba0-9a80a1012ede",
      "e5b70b84-954f-4696-8921-9299b42e24cb",
    ],
    pages: ["market", "mydeal"],
  },
  oreima: {
    groupIds: ["db45314a-b5a3-48f2-bcec-ca18fb566396"],
    pages: ["market"],
  },
  legal: {
    groupIds: [
      "b35bc056-ec20-4286-8889-96066e8bee8b",
      "9e2cb976-a5ba-4083-8522-7fd86b0490f3",
      "d27108f5-050e-4472-82eb-9faa8f355a10",
      "d9eb5867-17a2-4f33-8815-f2f026c243f4",
      // "77205305-d051-44e4-90f4-9a2e9af8418c",
    ],
    pages: ["legal"],
  },
  legalOreima: {
    groupIds: [
      "5b61ac9a-1073-4dd4-a37e-53f0ffe27065",
      // "1e8249a3-1126-4a0e-814e-3a6ff5ac13a0", // to delete, test group
    ],
    pages: ["oreima"],
  },
  compliance: {
    groupIds: ["08c6e304-a39e-4002-bb66-8002c450ea9b"],
    pages: ["market"],
  },
  middleOffice: {
    groupIds: ["463e31b8-9690-4706-a41f-e5ca2d650af6"],
    pages: ["market"],
  },
  assistantes: {
    groupIds: ["64731ea3-7a15-42bf-957d-79bf0b987070"],
    pages: ["market"],
  },
  helpDesk: {
    groupIds: ["2a102c4c-dd3b-4da7-a875-64b7a9852488"],
    pages: ["market"],
  },
  directionSI: {
    groupIds: ["e3207a7a-9293-45e5-97bf-5a4e97038b83"],
    pages: ["market"],
  },
  finance: {
    groupIds: ["49df65d3-d5f7-400c-a564-2e876693930b"],
    pages: ["market"],
  },
  legalSPO: {
    groupIds: ["9e2cb976-a5ba-4083-8522-7fd86b0490f3"],
    pages: ["market"],
  },
  assetManagement: {
    groupIds: ["d27108f5-050e-4472-82eb-9faa8f355a10"],
    pages: ["market"],
  },
  investorRelations: {
    groupIds: ["6b39091d-ea6f-4ce3-a29c-7c922d4d8bd4"],
    pages: ["market"],
  },
  admin: {
    groupIds: [
      "eb4845ef-f4f8-420e-8a6e-d3b8886a4a63", // Georges admin
      // "77205305-d051-44e4-90f4-9a2e9af8418c",
    ],
    pages: ["market", "mydeal", "legal", "oreima"],
  },
  management: {
    groupIds: [
      "dd808ee2-f678-4bbd-915e-0309e0f53bd8",
      // "77205305-d051-44e4-90f4-9a2e9af8418c",
    ],
    pages: ["market", "mydeal", "legal", "oreima"],
  },
} as const;

// Type for the available pages
export type AccessiblePage =
  (typeof accessGroups)[keyof typeof accessGroups]["pages"][number];

// Helper function to get all unique pages
export const getAllPages = () => {
  const pages = new Set<string>();
  Object.values(accessGroups).forEach((group) => {
    group.pages.forEach((page) => pages.add(page));
  });
  return Array.from(pages);
};
